import { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { VariablePopupState } from './TemplatedTextArea';
import { VariablePickerPanel } from '../VariablePickerPanel';
import { type VariableListItem, variableListItemRef } from '../variableUtils';
import { chipAttributes } from './templateSerialization';
import panelStyles from '../VariablePickerPanel.module.css';
import styles from './TextTemplateInput.module.css';
import { X } from 'lucide-react';

/** Minimum gap (px) between the popup and any viewport edge. */
export const POPUP_MARGIN = 4;

/**
 * Computes fixed positioning for the popup, clamped to viewport edges.
 */
function usePopupPosition(
  caretRect: DOMRect,
  popupEl: HTMLDivElement | null
): React.CSSProperties {
  let top = caretRect.bottom + POPUP_MARGIN;
  let left = caretRect.left;

  if (popupEl) {
    const { height, width } = popupEl.getBoundingClientRect();
    top = Math.max(
      POPUP_MARGIN,
      Math.min(top, window.innerHeight - height - POPUP_MARGIN)
    );
    left = Math.max(
      POPUP_MARGIN,
      Math.min(left, window.innerWidth - width - POPUP_MARGIN)
    );
  }

  return { position: 'fixed', top, left, zIndex: 1000 };
}

interface InlineVariablePickerProps {
  state: VariablePopupState;
  /** Called when user dismisses the popup — should delete the trigger char */
  onDeleteTrigger: () => void;
  /** When true, renders as a centred modal overlay instead of a caret-anchored popup */
  asModal?: boolean;
}

/**
 * Floating popup that appears at the text caret for inserting variable references.
 * When `asModal` is true, renders as a centred backdrop modal instead.
 */
export function InlineVariablePicker({
  state,
  onDeleteTrigger,
  asModal = false,
}: InlineVariablePickerProps) {
  const { items, command, clientRect } = state;
  const [popupEl, setPopupEl] = useState<HTMLDivElement | null>(null);
  const positionStyle = usePopupPosition(clientRect, popupEl);

  // Dismiss on click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popupEl && !popupEl.contains(e.target as Node)) {
        onDeleteTrigger();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [popupEl, onDeleteTrigger]);

  const variableItems = useMemo(() => items.map((i) => i.variable), [items]);

  const handleSelect = (picked: VariableListItem) => {
    const item = items.find((i) => i.variable === picked);
    if (item) {
      command({
        id: item.chipId,
        label: item.fullLabel,
        shortLabel: item.shortLabel,
        ...chipAttributes(variableListItemRef(picked)),
      });
    }
  };

  const pickerContent = (
    <>
      <div className={styles.pickerHeader}>
        <span aria-hidden='true' className={styles.pickerHeaderSpacer} />
        <button
          type='button'
          className={styles.pickerCloseIcon}
          aria-label='Close dynamic variables'
          onClick={onDeleteTrigger}
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>
      <div className={styles.pickerTitle}>Dynamic variables</div>
      <VariablePickerPanel
        items={variableItems}
        onSelect={handleSelect}
        onDismiss={onDeleteTrigger}
      />
      <div className={styles.pickerFooter}>
        <hr className={panelStyles.divider} />
        <button
          type='button'
          className={styles.closeButton}
          onClick={onDeleteTrigger}
        >
          Close
        </button>
      </div>
    </>
  );

  if (asModal) {
    return createPortal(
      <div className={styles.modalBackdrop} onMouseDown={onDeleteTrigger}>
        <div
          ref={setPopupEl}
          className={styles.modalCard}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {pickerContent}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div ref={setPopupEl} className={styles.pickerPopup} style={positionStyle}>
      {pickerContent}
    </div>
  );
}
